import { useLayoutEffect } from '@react-aria/utils';
import { forwardRef, useState } from 'react';
import type { ModalOverlayProps } from 'react-aria-components';
import {
  composeRenderProps,
  Modal as AriaModal,
  ModalOverlay,
} from 'react-aria-components';

import type { UnsafeStyleProps } from '@infinex/ui-styles';
import { cn, useResponsiveProp } from '@infinex/ui-styles';

import { RacOverlayVars, ModalVars } from './constants';
import { DialogTransitionEvents } from './DialogTransitionEvents';

// Main
// -----------------------------------------------------------------------------

export type ModalPrimitiveProps = {
  /**
   * The size of the dialog.
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large' | 'fullscreen';
  /**
   * Callback that is called when the open animation completes.
   */
  onOpenComplete?: () => void;
  /**
   * Callback that is called when the close animation completes.
   */
  onCloseComplete?: () => void;
} & Omit<ModalOverlayProps, 'className' | 'style'> &
  UnsafeStyleProps;

/**
 * Styled modal overlay. Prefer `<Dialog type="modal" />` in most cases.
 */
export const ModalPrimitive = forwardRef<HTMLDivElement, ModalPrimitiveProps>(
  function ModalPrimitive(props, ref) {
    const {
      className,
      isDismissable,
      isKeyboardDismissDisabled,
      size = 'medium',
      style,
    } = props;
    const [viewportGutter, setViewportGutter] = useState(8);

    const modalLayoutClasses = getModalLayoutClasses();
    const responsive = useResponsiveProp();
    useLayoutEffect(() => {
      setViewportGutter(
        size === 'fullscreen' ? 0 : responsive({ base: 8, md: 24 })!
      );
    }, [responsive, size]);

    return (
      <ModalOverlay
        isDismissable={isDismissable}
        isKeyboardDismissDisabled={isKeyboardDismissDisabled}
        onOpenChange={props.onOpenChange}
        className={cn(
          modalLayoutClasses.overlay,
          'entering:animate-in entering:fade-in entering:duration-100 entering:ease-linear',
          // "fill-mode-forwards" holds the exit end-state (invisible) if the
          // animation finishes before react-aria acknowledges it — otherwise a
          // dismissed overlay whose unmount is delayed pops back into view.
          'exiting:animate-out exiting:fade-out exiting:duration-200 exiting:delay-100 exiting:ease-linear exiting:fill-mode-forwards exiting:pointer-events-none'
        )}
        style={{ height: RacOverlayVars.pageHeight }}
      >
        <div
          className={cn(modalLayoutClasses.wrapper, 'grid place-items-center')}
        >
          <AriaModal
            ref={ref}
            className={cn(
              'bg-canvas relative shadow-xl',
              'entering:animate-in entering:slide-in-from-bottom-8 entering:fade-in entering:duration-300 entering:ease-in-out-back',
              // See the overlay's "fill-mode-forwards" note: keeps a dismissed
              // modal invisible if its unmount lags behind the exit animation.
              'exiting:animate-out exiting:slide-out-to-bottom-4 exiting:fade-out exiting:duration-300 exiting:ease-out-smooth exiting:fill-mode-forwards',

              // Definitive "width" for sizes so dialogs without enough content to
              // fill the horizontal space don't collapse. The "max-width" style
              // var will stop the dialog from growing beyond the viewport width.
              {
                'w-[448px]': size === 'small',
                'w-[576px]': size === 'medium',
                'w-[1024px]': size === 'large',
                'rounded-xlarge entering:zoom-in-95 exiting:zoom-out-90':
                  size !== 'fullscreen',
                'entering:ease-out-smooth size-full': size === 'fullscreen',
              },
              className
            )}
            style={{
              maxHeight: ModalVars.maxHeight,
              maxWidth: ModalVars.maxWidth,
              ...ModalVars.apply((vars) => ({
                maxHeight: `calc(${RacOverlayVars.viewportHeight} - ${vars.viewportGutter} * 2)`,
                maxWidth: `calc(100vw - ${vars.viewportGutter} * 2)`,
                padding: 32,
                viewportGutter,
              })),
              ...style,
            }}
          >
            {composeRenderProps(props.children, (children, renderProps) => (
              <DialogTransitionEvents
                onCloseComplete={props.onCloseComplete}
                onOpenComplete={props.onOpenComplete}
                {...renderProps}
              >
                {children}
              </DialogTransitionEvents>
            ))}
          </AriaModal>
        </div>
      </ModalOverlay>
    );
  }
);

// Helpers ---------------------------------------------------------------------

export const modalOverlayClasses = cn(
  'bg-overlay z-dialog absolute inset-0 isolate',
  RacOverlayVars.tw('h-(--page-height)')
);
export const modalWrapperClasses = cn(
  'sticky top-0 left-0 w-full',
  RacOverlayVars.tw('h-(--visual-viewport-height)')
);

/**
 * Position "absolute" + "sticky" solutions to resolve iOS 26 clipping issue.
 *
 * @see https://xcancel.com/devongovett/status/1968384768703349198
 */
export function getModalLayoutClasses() {
  return {
    overlay: modalOverlayClasses,
    wrapper: modalWrapperClasses,
  };
}
